import { CompactText } from 'ui/CompactText';
import React, {  useEffect, useMemo, useRef,  useState } from 'react';
import { PageHead } from 'ui/kit/PageHead';
import { useLocation, useNavigate } from 'react-router';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { useQueryClient } from '@tanstack/react-query';
import { addNotification } from 'notifications';
import { getPhotoHeaderStatus, useFetchPhotos } from 'hooks/usePhotos';
import { useGetCurrentProfile, useUpdateProfile } from 'modules/Profile/model/services';
import { useVerificationEdits } from 'hooks/useVerificationEdits';
import type { IProfile } from 'modules/Profile/model/types';
import { Button, TextField } from 'ui';
import { TiptapAboutEditor } from 'ui/TiptapAboutEditor';
import { normalizeAboutForStorage, sanitizeAboutHtml } from 'utils/aboutHtml';

import { teacherEditSchema } from './schema';
import documentLorem from '../../../assets/images/Lorem1.png';
import profileLorem from '../../../assets/images/Lorem2.png';
import { SelectField } from '../../../components/formsV2/fields/select';
import { PhotoUploadField } from '../../../components/formsV2/fields/uploadPhoto/index';
import { PriceAmountField } from '../../../components/formsV2/fields/PriceAmountField';
import styles from '../../../components/formsV2/fields/uploadPhoto/PhotoUploadField.module.scss';
import { FormContainer } from '../../../components/formsV2/formContainer';
import { useScrollToFormError } from '../../../components/formsV2/formContainer/AutoScrollToError';
import { ProfileContactsSection } from '../../../modules/Profile/components/ProfileContactsSection';
import { DEFAULT_SUBJECT } from '../../../modules/Details/NewTask/taskForm.store';
import { useCategories } from '../../../hooks/useCategories';
import { generateEndYearOptions, generateYearOptions } from '../../../utils/years';
import classes from '../Teacher.module.scss';
import edit from './TeacherEdit.module.scss';
import { hasTeacherFormChanges, teacherFormChangeRows } from '../utils/hasFormChanges';
import { newTutorItemId } from '../utils/tutorItemId';
import {
  nextTutorStep,
  TutorReviewPane,
  TutorStepBar,
  useTutorQuestionnaire,
} from './TutorQuestionnaire';
import { CabinetButton } from 'ui/kit/Kit';
import { UnsavedLeaveGuard } from 'ui/kit/UnsavedLeaveGuard';
import type {
  TeacherEditFormValues,
  TeacherEducationFormValues,
  TeacherPriceFormValues,
} from '../types';

import type { FormikErrors, FormikTouched } from 'formik';
const yearOptions = generateYearOptions();

type ServiceOptionSource = {
  name: string;
  title?: string | null;
  slug?: string | null;
};

function serviceLookupKey(value: string): string {
  return value.trim().toLowerCase().replace(/[\s-]+/g, '_');
}

function isMathCategory(cat: { name?: string | null; slug?: string | null; title?: string | null }): boolean {
  return cat.name === DEFAULT_SUBJECT || cat.slug === DEFAULT_SUBJECT || cat.title === 'Математика';
}

function findSubcategory<T extends ServiceOptionSource>(
  subs: T[] | undefined,
  stored: string,
): T | undefined {
  const raw = stored.trim();
  if (!raw || !subs?.length) return undefined;
  const key = serviceLookupKey(raw);
  return subs.find((sub) =>
    [sub.name, sub.slug, sub.title].some((item) => {
      if (!item) return false;
      const text = String(item);
      return text === raw || serviceLookupKey(text) === key;
    }),
  );
}

const TEACHER_GENERAL_ERROR_KEYS = [
  'name',
  'surname',
  'patronymic',
  'birthDate',
  'about',
  'trialLesson',
  'formatOnline',
  'formatOffline',
];

function stepForTeacherErrors(errors: object): string {
  const bag = errors as Record<string, unknown>;
  if (TEACHER_GENERAL_ERROR_KEYS.some((key) => bag[key])) return 'general';
  if (bag.educations) return 'education';
  if (bag.teacher_prices) return 'services';
  if (bag.profilePhoto) return 'photo';
  return 'review';
}

function firstErrorText(error: unknown): string | null {
  if (!error) return null;
  if (typeof error === 'string') return error;
  if (Array.isArray(error)) {
    for (const item of error) {
      const text = firstErrorText(item);
      if (text) return text;
    }
    return null;
  }
  if (typeof error === 'object') {
    for (const value of Object.values(error as Record<string, unknown>)) {
      const text = firstErrorText(value);
      if (text) return text;
    }
  }
  return null;
}

function touchFromErrors(error: unknown): unknown {
  if (!error) return undefined;
  if (typeof error === 'string') return true;
  if (Array.isArray(error)) return error.map((item) => touchFromErrors(item));
  if (typeof error === 'object') {
    return Object.fromEntries(
      Object.entries(error as Record<string, unknown>).map(([key, value]) => [
        key,
        touchFromErrors(value),
      ]),
    );
  }
  return true;
}

function profileToTeacherForm(profile: Partial<IProfile>): TeacherEditFormValues {
  const educations =
    profile.teacher_educations && profile.teacher_educations.length > 0
      ? profile.teacher_educations.map((currentEdu) => ({
          item_id: currentEdu?.item_id || newTutorItemId(),
          university: currentEdu?.university || '',
          startYear: currentEdu?.startYear?.toString() || '',
          endYear: currentEdu?.endYear?.toString() || '',
          faculty: currentEdu?.faculty || '',
          specialization: currentEdu?.specialization || '',
          testLesson: !currentEdu?.endYear,
          eduPhoto: currentEdu?.diploma_photo || null,
          token: currentEdu?.diploma_photo || '',
        }))
      : [];
  const teacher_prices =
    profile.teacher_prices && profile.teacher_prices.length > 0
      ? profile.teacher_prices.map((currentPrice) => ({
          item_id: currentPrice?.item_id || newTutorItemId(),
          subject: currentPrice?.category_name || DEFAULT_SUBJECT,
          service: currentPrice?.subcategory_name || '',
          price: String(currentPrice?.price ?? ''),
        }))
      : [
          {
            item_id: newTutorItemId(),
            subject: DEFAULT_SUBJECT,
            service: '',
            price: '',
          },
        ];
  return {
    name: profile.name || '',
    surname: profile.surname || '',
    patronymic: profile.patronymic || '',
    birthDate: (profile.date_of_birth || '').slice(0, 10),
    formatOnline: Boolean(profile.format_online),
    formatOffline: Boolean(profile.format_offline),
    about: sanitizeAboutHtml(profile.about || ''),
    trialLesson: profile.trial_lesson ? 'agree' : 'decline',
    profilePhoto: profile.profile_photo_token || null,
    educations,
    teacher_prices,
  };
}

// Подключаем парсинг кастомных форматов
dayjs.extend(customParseFormat);

export const TeacherEdit = () => {
  const queryClient = useQueryClient();
  const { data: categories, isLoading } = useCategories();
  const { data: currentProfile } = useGetCurrentProfile();
  const updateProfile = useUpdateProfile();
  const verificationEdits = useVerificationEdits();
  const navigate = useNavigate();
  const location = useLocation();
  const photoUrls = React.useMemo(() => {
    if (!currentProfile) return [];

    const urls: string[] = [];

    // Фото профиля
    if (currentProfile.profile_photo_token) {
      urls.push(currentProfile.profile_photo_token);
    }

    // Фото дипломов
    currentProfile.teacher_educations?.forEach((edu) => {
      if (edu?.diploma_photo) {
        urls.push(edu?.diploma_photo);
      }
    });

    return urls;
  }, [currentProfile]);

  const { data: photosDataLive } = useFetchPhotos(photoUrls);

  const [formReady, setFormReady] = useState(false);
  const [step, setStepRaw] = useState('general');
  // p7: старый шаг «Стоимость» (price) = те же поля, что «Услуги». Ведём на единый шаг.
  const setStep = React.useCallback((key: string) => setStepRaw(key === 'price' ? 'services' : key), []);
  const questionnaire = useTutorQuestionnaire();
  const saveOkRef = useRef(false);
  const resetFormRef = useRef<((values: TeacherEditFormValues) => void) | null>(null);
  const alignPricesRef = useRef<(() => void) | null>(null);
  const [bootstrappedValues, setBootstrappedValues] =
    useState<TeacherEditFormValues | null>(null);

  const emptyEdu = {
    item_id: newTutorItemId(),
    university: '',
    faculty: '',
    specialization: '',
    startYear: '',
    endYear: '',
    testLesson: false,
    eduPhoto: null,
    token: '',
  };

  useEffect(() => {
    if (!currentProfile || formReady) return;

    setBootstrappedValues(profileToTeacherForm(currentProfile));
    setFormReady(true);
  }, [currentProfile, formReady]);

  const photoStatuses = useMemo(() => {
    const map: Record<string, string> = {};

    photosDataLive?.forEach((photo) => {
      const status = getPhotoHeaderStatus(photo.headers);
      if (status) {
        map[photo.token] = status;
      }
    });

    // Статусы из API профиля перекрывают заголовки get_image (актуально после модерации).
    const profileToken = currentProfile?.profile_photo_token;
    if (profileToken && currentProfile?.profile_photo_status) {
      map[profileToken] = currentProfile.profile_photo_status;
    }
    currentProfile?.teacher_educations?.forEach((edu) => {
      const token = edu?.diploma_photo;
      if (token && edu?.diploma_photo_status) {
        map[token] = edu.diploma_photo_status;
      }
    });

    return map;
  }, [photosDataLive, currentProfile]);

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['photos-full'] });
  }, [currentProfile?.verification_status, queryClient]);

  useEffect(() => {
    alignPricesRef.current?.();
  }, [categories]);

  useEffect(() => {
    if (!formReady) return;
    const rawHash = location.hash?.replace(/^#/, '');
    if (!rawHash) return;
    const hash = rawHash === 'price' ? 'services' : rawHash;
    if (['general', 'education', 'services', 'photo', 'review'].includes(hash)) setStep(hash);
    const timer = window.setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [formReady, location.hash]);

  // Опции для радио-кнопок
  const trialLessonOptions = [
    { value: 'agree', label: 'Согласен' },
    { value: 'decline', label: 'Нет, пока воздержусь' },
  ];

  const getServiceOptions = React.useCallback(() => {
    if (!categories) return [];

    const category = categories.find((cat) => isMathCategory(cat));
    if (!category?.subcategories) return [];

    return category.subcategories.map((sub) => ({
      value: sub.name,
      label: sub.title || sub.name,
    }));
  }, [categories]);

  const handleSubmit = async (values: TeacherEditFormValues) => {
    const formData = new FormData();

    // 1. Базовые поля. ФИО и дату рождения нельзя менять, если они уже заполнены.
    formData.append('about', normalizeAboutForStorage(values.about || ''));
    formData.append('formatOnline', values.formatOnline.toString());
    formData.append('formatOffline', values.formatOffline.toString());
    formData.append('trialLesson', String(values.trialLesson === 'agree'));

    const savedName = (currentProfile?.name || '').trim();
    const savedSurname = (currentProfile?.surname || '').trim();
    const savedPatronymic = (currentProfile?.patronymic || '').trim();
    const savedBirth = (currentProfile?.date_of_birth || '').slice(0, 10);
    if (!savedName && values.name.trim()) {
      formData.append('name', values.name.trim());
    }
    if (!savedSurname && values.surname.trim()) {
      formData.append('surname', values.surname.trim());
    }
    if (!savedPatronymic && values.patronymic.trim()) {
      formData.append('patronymic', values.patronymic.trim());
    }
    if (!savedBirth && values.birthDate) {
      formData.append('date_of_birth', values.birthDate);
    }

    // 2. Фото профиля
    if (values.profilePhoto instanceof File) {
      formData.append('profilePhoto', values.profilePhoto);
    }

    const filledEducations = values.educations.filter((education) => {
      const hasFile = education.eduPhoto instanceof File;
      const hasExistingPhoto = Boolean(education.token) || Boolean(education.eduPhoto);
      return [
        education.university,
        education.faculty,
        education.specialization,
        education.startYear,
        education.endYear,
      ].some((value) => value?.trim()) || hasFile || hasExistingPhoto;
    });

    // 3. Образование: при добавлении записи все поля обязательны (см. schema).
    // "Учусь по настоящее время" - endYear пустой, diploma не обязателен.
    filledEducations.forEach((education, index) => {
      formData.append(`educations[${index}][item_id]`, education.item_id);
      formData.append(`educations[${index}][university]`, education.university);
      formData.append(`educations[${index}][faculty]`, education.faculty);
      formData.append(`educations[${index}][specialization]`, education.specialization);
      formData.append(`educations[${index}][startYear]`, education.startYear);

      formData.append(
        `educations[${index}][endYear]`,
        education.testLesson ? '' : education.endYear,
      );

      // Файл документа об образовании
      if (education.eduPhoto instanceof File) {
        formData.append(`educations[${index}][eduPhoto]`, education.eduPhoto);
      } else if (education.token) {
        formData.append(`educations[${index}][diploma_photo_token]`, education.token);
      } else if (typeof education.eduPhoto === 'string' && education.eduPhoto) {
        const urlMatch = education.eduPhoto.match(/\/get_image\/([^/?]+)/);
        if (urlMatch?.[1]) {
          formData.append(`educations[${index}][diploma_photo_token]`, urlMatch[1]);
        } else if (!education.eduPhoto.startsWith('blob:')) {
          formData.append(`educations[${index}][diploma_photo_token]`, education.eduPhoto);
        }
      }
    });

    // 4. Цены в JSON формате
    if (values.teacher_prices && values.teacher_prices.length > 0) {
      const pricesForApi = values.teacher_prices.map((price) => {
        const category = categories?.find((cat) => isMathCategory(cat));
        const subcategory = findSubcategory(category?.subcategories, price.service);

        return {
          item_id: price.item_id,
          price: parseInt(price.price) || 0,
          token: '',
          category_name: category?.slug || category?.name || DEFAULT_SUBJECT,
          category_title: category?.title || 'Математика',
          subcategory_name:
            subcategory?.slug || subcategory?.name || String(price.service || '').trim(),
          subcategory_title: subcategory?.title || subcategory?.name || String(price.service || '').trim(),
        };
      });

      formData.append('teacher_prices', JSON.stringify(pricesForApi));
    }

    // 5. Отладка - проверка FormData
    console.log(' Проверка FormData перед отправкой:');
    for (const [key, value] of formData.entries()) {
      if (value instanceof File) {
        console.log(
          ` ${key}: File - ${value.name} (${value.type}, ${value.size} байт)`,
        );
      } else {
        console.log(` ${key}:`, value);
      }
    }

    // 6. Отправка на сервер. После сохранения остаемся на шаге «Проверка».
    saveOkRef.current = false;
    const wasApproved = currentProfile?.verification_status === 'approved';
    try {
      const saved = await updateProfile.mutateAsync(formData);
      const nextValues = profileToTeacherForm(saved || {});
      setBootstrappedValues(nextValues);
      resetFormRef.current?.(nextValues);
      setStep('review');
      const queued = saved?.verification_status === 'pending_review' || wasApproved;
      addNotification(
        queued
          ? 'Правки отправлены на проверку. Пока их не одобрят, ученики видят прежнюю анкету.'
          : 'Изменения сохранены. Когда анкета будет собрана, ее можно отправить на проверку.',
        'success',
      );
      saveOkRef.current = true;
    } catch (error) {
      console.error(' Ошибка при отправке формы:', error);
      addNotification('Не удалось сохранить анкету. Проверьте поля и попробуйте еще раз.', 'error');
      saveOkRef.current = false;
    }
  };

  if (!formReady || !bootstrappedValues) {
    return (
      <main className={edit.page}>
        <p>Загрузка формы...</p>
      </main>
    );
  }

  return (
    <FormContainer<TeacherEditFormValues>
      initialValues={bootstrappedValues}
      onSubmit={handleSubmit}
      validationSchema={teacherEditSchema}
      validateOnChange={true}
      validateOnBlur={true}
      enableReinitialize={false}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        setFieldValue,
        setFieldTouched,
        setTouched,
        isSubmitting,
        dirty,
        resetForm,
        validateForm,
        submitForm,
      }) => {
        resetFormRef.current = (next) => resetForm({ values: next });
        alignPricesRef.current = () => {
          const subs = categories?.find((cat) => isMathCategory(cat))?.subcategories;
          if (!subs?.length) return;
          const alignRows = (rows: TeacherPriceFormValues[]) => {
            let changed = false;
            const next = rows.map((price) => {
              const sub = findSubcategory(subs, price.service);
              if (!sub || sub.name === price.service) return price;
              changed = true;
              return { ...price, service: sub.name };
            });
            return changed ? next : null;
          };
          const nextPrices = alignRows(values.teacher_prices);
          if (!nextPrices) return;
          const nextInitial = bootstrappedValues
            ? {
                ...bootstrappedValues,
                teacher_prices:
                  alignRows(bootstrappedValues.teacher_prices) || bootstrappedValues.teacher_prices,
              }
            : { ...values, teacher_prices: nextPrices };
          setBootstrappedValues(nextInitial);
          if (!dirty) {
            resetForm({ values: { ...values, teacher_prices: nextPrices } });
            return;
          }
          setFieldValue('teacher_prices', nextPrices, false);
        };
        const canSave =
          (dirty || hasTeacherFormChanges(values, bootstrappedValues)) &&
          !updateProfile.isPending;
        const localChanges = teacherFormChangeRows(values, bootstrappedValues, (slug) => {
          const category = categories?.find((item) => isMathCategory(item));
          const sub = findSubcategory(category?.subcategories, slug);
          return sub?.title || slug;
        });
        useScrollToFormError(errors, isSubmitting, {
          errorClass: 'form-error-message',
          scrollBehavior: 'smooth', // Плавный скролл
          scrollBlock: 'center',
          scrollDelay: 200,
          focusField: true,
        });
        return (
          <>
          <UnsavedLeaveGuard
            when={canSave}
            audience="tutor"
            onSave={async () => {
              const formErrors = await validateForm();
              if (formErrors && Object.keys(formErrors).length > 0) {
                setStep(stepForTeacherErrors(formErrors));
                setTouched(
                  touchFromErrors(formErrors) as FormikTouched<TeacherEditFormValues>,
                  false,
                );
                addNotification(
                  firstErrorText(formErrors) || 'Проверьте поля и сохраните еще раз.',
                  'error',
                );
                return false;
              }
              await submitForm();
              return saveOkRef.current;
            }}
          />
          <main className={edit.page}>
            <PageHead crumb={<>Личный кабинет / Мой профиль</>} title={<>{step === 'general'
                ? 'Общие сведения'
                : step === 'education'
                  ? 'Образование'
                  : step === 'services'
                    ? 'Услуги и цены'
                    : step === 'photo'
                        ? 'Фото'
                        : 'Отправка профиля на проверку'}</>} />
            <TutorStepBar current={step} onChange={setStep} />
            <div hidden={step !== 'general'}>
            <div className={edit.card}>
            <h1 id='general'>Имя и контакты</h1>
            {!currentProfile?.surname || !currentProfile?.date_of_birth ? (
              <p className={`fig-field-error ${edit.noticeText}`} role='note'>
                Укажите фамилию и дату рождения - без них анкету не отправить на проверку. После сохранения эти поля уже не изменить.
              </p>
            ) : (
              <p className={edit.noticeText} role='note'>
                Имя, фамилия, отчество и дата рождения не меняются.
              </p>
            )}

            {/* Блок ФИО */}
            <div className={classes['block-stat']}>
              <div className={classes['block-stat-NameFamily']}>
                {/* Figma 20:4091: поля стопкой с зазором 16, подпись не налезает на поле выше. */}
                <div
                  className={classes['block-lk']}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: 16,
                    maxWidth: 'none',
                    width: '100%',
                  }}
                >

                  <div className={classes['field-wrapper-lk']}>
                    <TextField
                      shape='brick'
                      placeholder='Иван'
                      label='Имя'
                      size='lg'
                      name='name'
                      value={values.name}
                      disabled={Boolean((currentProfile?.name || '').trim())}
                      onChange={(val) => setFieldValue('name', val)}
                      onBlur={handleBlur}
                    />
                  </div>

                  <div className={classes['field-wrapper-lk']}>
                    <TextField
                      shape='brick'
                      placeholder='Иванов'
                      label='Фамилия'
                      size='lg'
                      name='surname'
                      value={values.surname}
                      disabled={Boolean((currentProfile?.surname || '').trim())}
                      onChange={(val) => setFieldValue('surname', val)}
                      onBlur={handleBlur}
                    />
                  </div>

                  <div className={classes['field-wrapper-lk']}>
                    <TextField
                      shape='brick'
                      placeholder='Иванович'
                      label='Отчество'
                      size='lg'
                      name='patronymic'
                      value={values.patronymic}
                      disabled={Boolean((currentProfile?.patronymic || '').trim())}
                      onChange={(val) => setFieldValue('patronymic', val)}
                      onBlur={handleBlur}
                    />
                  </div>

                  <div className={classes['field-wrapper-lk']}>
                    <TextField
                      shape='brick'
                      placeholder='ДД.ММ.ГГГГ'
                      label='Дата рождения'
                      size='lg'
                      type='date'
                      name='birthDate'
                      value={values.birthDate}
                      disabled={Boolean((currentProfile?.date_of_birth || '').slice(0, 10))}
                      onChange={(val) => setFieldValue('birthDate', val)}
                      onBlur={handleBlur}
                    />
                  </div>
                </div>
              </div>
            </div>

            <ProfileContactsSection />
            </div>
            </div>

            {/* Блок фотографии */}
            <div hidden={step !== 'photo'}>
            <div className={edit.card}>
            <h1 id='photo'>Фото</h1>
            <div className={classes['photo-upload']}>
              <div className={classes['photo-upload__content']}>
                <h4>Критерии вашего фото</h4>
                <ol>
                  <li>
                    Фото в деловом стиле: одежда строгого покроя (рубашка, блузка,
                    пиджак), без ярких принтов и отвлекающих деталей.
                  </li>
                  <li>
                    Кадрирование по плечи: лицо и верхняя часть туловища четко видны, без
                    лишнего пространства сверху и с боков.
                  </li>
                  <li>
                    Нейтральный и однотонный фон (серый, бежевый, белый) - без узоров,
                    окон, дверей или посторонних предметов.
                  </li>
                  <li>
                    Естественное или ровное искусственное освещение: без теней, бликов,
                    эффекта "красных глаз" и чрезмерной обработки.
                  </li>
                </ol>
                <h5>
                  При загрузке фотографии профиля, изображение сначала проходит модерацию.
                </h5>
                <h5>Поддерживаемые форматы: jpg, png.</h5>
                <h5>
                  Фото 1:1 или 3:4, от 100×100 до 2000×2000 пикселей, до 200 МБ.
                  Сильно вытянутые снимки не принимаются - в профиле фото показывается с
                  обрезкой по краям, без растягивания.
                </h5>
              </div>
              <PhotoUploadField
                name='profilePhoto'
                status={photoStatuses[currentProfile?.profile_photo_token || ''] || ''}
                placeholderImage={profileLorem}
                width={250}
                height={334}
                avatarRules
              />
            </div>
            </div>
            </div>

            {/* Блок образования */}
            <div hidden={step !== 'education'} className={edit.card}>
            <h1 id='education'>Образование</h1>
            {values.educations.length === 0 && (
              <div className={classes['teacher-edit-btn-row']}>
                <Button
                  type='button'
                  className={styles.addEducationButton}
                  variant={'filled'}
                  size={'md'}
                  color={'primary'}
                  shape={'brick'}
                  onClick={() => setFieldValue('educations', [emptyEdu])}
                >
                  + Добавить образование
                </Button>
              </div>
            )}
            {values.educations.map((education, index) => {
              const educationErrors =
                typeof errors.educations?.[index] === 'object'
                  ? (errors.educations?.[
                      index
                    ] as FormikErrors<TeacherEducationFormValues>)
                  : undefined;
              const educationTouched = touched.educations?.[index];

              return (
                <div key={index} className={classes['teacher-doc']}>
                  <div className={classes['teacher-doc__content']}>
                    <div>
                      <TextField
                        shape={'brick'}
                        placeholder={'Название учебного заведения'}
                        label='Учебное заведение'
                        size={'lg'}
                        name={`educations[${index}].university`}
                        value={education.university}
                        onChange={(value) => {
                          setFieldValue(`educations[${index}].university`, value);
                          if (educationErrors?.university) {
                            setFieldTouched(`educations[${index}].university`, false);
                          }
                        }}
                        onBlur={handleBlur}
                      />
                      {educationTouched?.university && educationErrors?.university && (
                        <div
                          className='form-error-message'
                          style={{ color: 'red', marginTop: 4 }}
                        >
                          {educationErrors.university}
                        </div>
                      )}

                      <TextField
                        shape={'brick'}
                        placeholder={'Факультет/Специальность/Отделение'}
                        label='Факультет / отделение'
                        size={'lg'}
                        name={`educations[${index}].faculty`}
                        value={education.faculty}
                        onChange={(value) => {
                          setFieldValue(`educations[${index}].faculty`, value);
                          if (educationErrors?.faculty) {
                            setFieldTouched(`educations[${index}].faculty`, false);
                          }
                        }}
                        onBlur={handleBlur}
                      />
                      {educationTouched?.faculty && educationErrors?.faculty && (
                        <div
                          className='form-error-message'
                          style={{ color: 'red', marginTop: 4 }}
                        >
                          {educationErrors.faculty}
                        </div>
                      )}

                      <TextField
                        shape={'brick'}
                        placeholder={'Специализация'}
                        label='Специализация'
                        size={'lg'}
                        name={`educations[${index}].specialization`}
                        value={education.specialization}
                        onChange={(value) => {
                          setFieldValue(`educations[${index}].specialization`, value);
                          if (educationErrors?.specialization) {
                            setFieldTouched(`educations[${index}].specialization`, false);
                          }
                        }}
                        onBlur={handleBlur}
                      />
                      {educationTouched?.specialization &&
                        educationErrors?.specialization && (
                          <div
                            className='form-error-message'
                            style={{ color: 'red', marginTop: 4 }}
                          >
                            {educationErrors.specialization}
                          </div>
                        )}

                      <div className={classes['date-doc']}>
                        {/* Дата поступления с маской */}
                        <SelectField
                          name={`educations[${index}].startYear`}
                          label='Год поступления'
                          options={yearOptions}
                          placeholder='Выберите год'
                          value={education.startYear}
                          onChange={(value: string) => {
                            setFieldValue(`educations[${index}].startYear`, value);
                            if (educationErrors?.startYear) {
                              setFieldTouched(`educations[${index}].startYear`, false);
                            }

                            // Если выбранный год окончания раньше нового года поступления,
                            // сбрасываем год окончания
                            if (education.endYear && education.endYear !== 'present') {
                              const endYear = parseInt(education.endYear);
                              const newStartYear = parseInt(value);
                              if (endYear < newStartYear) {
                                setFieldValue(`educations[${index}].endYear`, '');
                              }
                            }
                          }}
                        />

                        <SelectField
                          name={`educations[${index}].endYear`}
                          label='Год окончания'
                          options={generateEndYearOptions(education.startYear)}
                          placeholder='Выберите год'
                          value={education.endYear}
                          onChange={(value: string) => {
                            setFieldValue(`educations[${index}].endYear`, value);
                            if (educationErrors?.endYear) {
                              setFieldTouched(`educations[${index}].endYear`, false);
                            }
                          }}
                          isDisabled={education.testLesson}
                        />
                      </div>
                      {(educationTouched?.startYear && educationErrors?.startYear) ||
                      (educationTouched?.endYear && educationErrors?.endYear) ? (
                        <div
                          className='form-error-message'
                          style={{ color: 'red', marginTop: 4 }}
                        >
                          {educationErrors?.startYear || educationErrors?.endYear}
                        </div>
                      ) : null}
                      <div className={classes['teacher-work']}>
                        <label>
                          <input
                            type='checkbox'
                            name='testLesson'
                            checked={education.testLesson}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setFieldValue(`educations[${index}].testLesson`, isChecked);
                              // Если включаем чекбокс - очищаем поле окончания и сбрасываем ошибку диплома
                              if (isChecked) {
                                setFieldValue(`educations[${index}].endYear`, '');
                                setFieldTouched(`educations[${index}].eduPhoto`, false);
                              }
                            }}
                          />
                          <span className={classes['custom-checkbox']}></span>
                          <span>Учусь по настоящее время</span>
                        </label>
                      </div>
                    </div>
                    <div>
                      <PhotoUploadField
                        name={`educations[${index}].eduPhoto`}
                        status={photoStatuses[education?.token || ''] || ''}
                        placeholderImage={documentLorem}
                        width={250}
                        height={167}
                      />
                      {educationTouched?.eduPhoto && educationErrors?.eduPhoto ? (
                        <div
                          className='form-error-message'
                          style={{ color: 'red', marginTop: 4 }}
                        >
                          {String(educationErrors.eduPhoto)}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className={classes['teacher-edit-btn-row']}>
                    <Button
                      type='button'
                      variant={'outlined'}
                      size={'md'}
                      color={'error'}
                      shape={'brick'}
                      onClick={() => {
                        const newEducations = values.educations.filter(
                          (_, i) => i !== index,
                        );
                        setFieldValue('educations', newEducations);
                      }}
                    >
                       Удалить
                    </Button>
                  </div>

                  <div className={classes['teacher-edit-btn-row']}>
                    {index === values.educations.length - 1 &&
                      values.educations.length < 5 && (
                        <Button
                          type='button'
                          className={styles.addEducationButton}
                          variant={'filled'}
                          size={'md'}
                          color={'primary'}
                          shape={'brick'}
                          onClick={() =>
                            setFieldValue('educations', [
                              ...values.educations,
                              {
                                item_id: newTutorItemId(),
                                university: '',
                                faculty: '',
                                specialization: '',
                                startYear: '',
                                endYear: '',
                                testLesson: false,
                                eduPhoto: null,
                                token: '',
                              },
                            ])
                          }
                        >
                          + Добавить образование
                        </Button>
                      )}
                  </div>
                </div>
              );
            })}
            </div>
            <div hidden={step !== 'general'} className={edit.card}>
            {/* Блок "Расскажи о себе" */}
            <h1 id='about'>О себе и формат урока</h1>
            <TiptapAboutEditor
              value={values.about}
              onChange={(html) => setFieldValue('about', html)}
              onBlur={() => setFieldTouched('about', true)}
              hasError={Boolean(touched.about && errors.about)}
            />
            {touched.about && errors.about && (
              <div className='form-error-message' style={{ color: 'red', marginTop: 4 }}>
                {errors.about}
              </div>
            )}

            <h1>Формат занятий и область работы</h1>
            <div className={classes['teacher-work']}>
              <h2>Согласие на пробное занятие</h2>
              <div className={classes.radioGroup}>
                {trialLessonOptions.map((option) => (
                  <label key={option.value} className={classes.radioItem}>
                    <input
                      type='radio'
                      name='trialLesson'
                      value={option.value}
                      checked={values.trialLesson === option.value}
                      onChange={(e) => setFieldValue('trialLesson', e.target.value)}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className={classes['teacher-work']}>
              <h2>Как вы проводите занятия</h2>
              <label>
                <input
                  type='checkbox'
                  name='formatOnline'
                  checked={values.formatOnline}
                  onChange={(e) => setFieldValue('formatOnline', e.target.checked)}
                />
                <span className={classes['custom-checkbox']}></span>
                <span>Онлайн</span>
              </label>
              <label>
                <input
                  type='checkbox'
                  name='formatOffline'
                  checked={values.formatOffline}
                  onChange={(e) => setFieldValue('formatOffline', e.target.checked)}
                />
                <span className={classes['custom-checkbox']}></span>
                <span>Очно</span>
              </label>
            </div>
            </div>

            {/* Блок карты без полей ввода */}
            {/* {values.formatOffline && (
            <div className={classes['field-wrapper-map']}>
              <div className={classes['map-container']}>
                <div
                  style={{
                    background: '#f5f5f5',
                    height: '300px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '15px',
                  }}
                >
                  Карта будет здесь
                </div>
              </div>
            </div>
          )} */}

            {/* Блок услуг */}
            <div hidden={step !== 'services'} className={edit.card}>
            <h1 id='services'>Услуги и цены</h1>
            <p>Выберите направления, по которым вы занимаетесь, и укажите цену часа для каждого.</p>
            {values.teacher_prices.map((price, index) => {
              const allServiceOptions = getServiceOptions();
              const selectedElsewhere = new Set(
                values.teacher_prices
                  .map((row, rowIndex) =>
                    rowIndex === index ? null : String(row.service || '').trim(),
                  )
                  .filter(Boolean) as string[],
              );
              const storedService = String(price.service || '').trim();
              const serviceCategory = categories?.find((cat) => isMathCategory(cat));
              const matchedService = findSubcategory(
                serviceCategory?.subcategories,
                storedService,
              );
              let serviceOptions = allServiceOptions.filter(
                (option) =>
                  !selectedElsewhere.has(option.value) || option.value === storedService,
              );
              if (
                storedService &&
                !serviceOptions.some((option) => option.value === storedService)
              ) {
                if (matchedService?.name) {
                  serviceOptions = serviceOptions.filter(
                    (option) => option.value !== matchedService.name,
                  );
                }
                serviceOptions = [
                  {
                    value: storedService,
                    label: matchedService?.title || matchedService?.name || storedService,
                  },
                  ...serviceOptions,
                ];
              }
              const priceErrors =
                typeof errors.teacher_prices?.[index] === 'object'
                  ? (errors.teacher_prices?.[
                      index
                    ] as FormikErrors<TeacherPriceFormValues>)
                  : undefined;
              const priceTouched = touched.teacher_prices?.[index];

              return (
                <div key={index} className={classes['teacher-price']}>
                  <div className={classes['teacher-price__content']}>
                    <div>
                      <SelectField
                        name={`teacher_prices[${index}].service`}
                        label='Услуга'
                        options={serviceOptions}
                        isDisabled={isLoading || allServiceOptions.length === 0}
                        placeholder='Выберите услугу'
                      />
                      {typeof errors.teacher_prices === 'string' &&
                      index === values.teacher_prices.length - 1 ? (
                        <div className='form-error-message' style={{ color: 'red', marginTop: 4 }}>
                          {errors.teacher_prices}
                        </div>
                      ) : null}
                      <div className={classes['date-doc']}>
                        <div className={classes['teacher-price-input']}>
                          <PriceAmountField
                            shape='brick'
                            label='Цена'
                            placeholder='0'
                            size='lg'
                            value={String(price.price ?? '')}
                            onChange={(value) => {
                              setFieldValue(`teacher_prices[${index}].price`, value);
                              if (priceErrors?.price) {
                                setFieldTouched(`teacher_prices[${index}].price`, false);
                              }
                            }}
                          />
                          <span className={classes['teacher-price-input__suffix']}>₽/час</span>
                        </div>
                        {priceTouched?.price && priceErrors?.price && (
                          <div
                            className='form-error-message'
                            style={{ color: 'red', marginTop: 4 }}
                          >
                            {priceErrors.price}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={classes['teacher-edit-btn-row']}>
                      {index > 0 && (
                        <Button
                          type='button'
                          variant={'outlined'}
                          size={'md'}
                          color={'error'}
                          shape={'brick'}
                          onClick={() => {
                            const newPrices = values.teacher_prices.filter(
                              (_, i) => i !== index,
                            );
                            setFieldValue('teacher_prices', newPrices);
                          }}
                        >
                          <div className={classes['gr_snap_text_delet']}> Удалить </div>
                        </Button>
                      )}
                    </div>
                    <div className={classes['teacher-edit-btn-row']}>
                      {index === values.teacher_prices.length - 1 &&
                        values.teacher_prices.length < 5 &&
                        allServiceOptions.some(
                          (option) =>
                            !values.teacher_prices.some(
                              (row) => String(row.service || '').trim() === option.value,
                            ),
                        ) && (
                          <Button
                            type='button'
                            className={classes['add-service-button']}
                            variant={'filled'}
                            size={'md'}
                            color={'primary'}
                            shape={'brick'}
                            onClick={() =>
                              setFieldValue('teacher_prices', [
                                ...values.teacher_prices,
                                {
                                  item_id: newTutorItemId(),
                                  subject: DEFAULT_SUBJECT,
                                  service: '',
                                  price: '',
                                },
                              ])
                            }
                          >
                            <div className={classes['gr_snap_text']}>
                              + Добавить услугу{' '}
                            </div>
                          </Button>
                        )}
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
            <div hidden={step !== 'review'} className={edit.card}>
            <TutorReviewPane localChanges={localChanges} values={values} />
            {verificationEdits.pendingEdits && !verificationEdits.canRevert ? (
              <p className={edit.noticeText}>
                Пока проверка идет, можно изменить поле и отправить его снова. Ученики до решения видят прежнюю анкету.
              </p>
            ) : null}
            {canSave ? (
              <CompactText className={edit.noticeText} tone={'info'}>
                Есть несохраненные изменения. Сохраните их, чтобы отправить анкету на проверку.
              </CompactText>
            ) : null}
            <div className={edit.actions}>
              <Button
                className={classes['teacher-edit-save-btn']}
                variant={'filled'}
                size={'md'}
                color={'primary'}
                shape={'brick'}
                type='button'
                loading={updateProfile.isPending}
                onClick={async (event) => {
                  event.preventDefault();
                  if (updateProfile.isPending) return;
                  if (!canSave) {
                    addNotification(
                      'Пока нет новых правок. Измените анкету и сохраните еще раз.',
                      'info',
                    );
                    return;
                  }
                  const formErrors = await validateForm();
                  if (formErrors && Object.keys(formErrors).length > 0) {
                    setStep(stepForTeacherErrors(formErrors));
                    setTouched(
                      touchFromErrors(formErrors) as FormikTouched<TeacherEditFormValues>,
                      false,
                    );
                    addNotification(
                      firstErrorText(formErrors) || 'Проверьте поля на этом шаге.',
                      'error',
                    );
                    return;
                  }
                  await submitForm();
                }}
              >
                {questionnaire.readyToSubmit ? 'Отправить на проверку' : 'Сохранить'}
              </Button>
              <Button
                type='button'
                variant={'outlined'}
                size={'md'}
                color={'primary'}
                shape={'brick'}
                onClick={() => navigate('/teacher')}
              >
                К анкете
              </Button>
            </div>
            </div>
            {step !== 'review' ? (
              <div className={edit.card}>
                <div className={edit.notice}>
                  <p className={edit.noticeTitle}>Перед сохранением</p>
                  <CompactText
                    className={edit.noticeText}
                    tone={currentProfile?.verification_status === 'approved' ? 'info' : 'warning'}
                  >
                    {currentProfile?.verification_status === 'approved'
                      ? 'Анкета одобрена. Правки уйдут на проверку, а ученики до одобрения будут видеть текущую версию.'
                      : 'Изменения в анкете не означают одобрение профиля. Поиск и чаты пока закрыты.'}
                  </CompactText>
                </div>
                <div className={edit.actions}>
                  <CabinetButton
                    type='button'
                    onClick={() => {
                      const next = nextTutorStep(questionnaire.steps, step);
                      if (next) setStep(next);
                    }}
                  >
                    Дальше
                  </CabinetButton>
                </div>
              </div>
            ) : null}
          </main>
          </>
        );
      }}
    </FormContainer>
  );
};
